import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useStateMachine } from 'little-state-machine';
import { updateAction } from '../MultipartForm/stateMachineActions';

import FormStepWrapper from './FormStepWrapper';
import { MPFStepProps, StudentData, generateBlankStudentData } from '../../util/types';
import StudentInfo from '../MultipartForm/StudentInfo';
import TextInput from '../MultipartForm/Fields/TextInput';
import TextArea from '../MultipartForm/Fields/TextArea';
import RadioButton from '../MultipartForm/Fields/RadioButton';

import splitStudentsFromFormData from '../MultipartForm/splitStudentsFromFormData';
import generateErrorMarkup from '../MultipartForm/generateErrorMarkup';
import generateMaxLengthValidateFn from '../MultipartForm/generateMaxLengthValidateFn';

// TODO: put 90% of this into a super class or interface or something
const Step1 = (props: MPFStepProps) => {
  const MAX_GROUP_MEMBERS = 10;
  const { register, unregister, control, getValues, setValue, handleSubmit, formState: { errors } } = useForm();
  const { actions, state } = useStateMachine({ updateAction });
  // Persists form data (doesn't validate!)
  const applyForm = (e?: React.MouseEvent) => {
    const formData = getValues();
    actions.updateAction({
      formData,
    });
  };
  const onSubmit = (data: any) => {
    // console.debug('onSubmit', data);
    actions.updateAction({
      formData: data,
    });
    props.next();
    // props.history.push('./step2');
  };
  // const onBack = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   props.prev();
  // };

  // Grab student data or add an empty

  const [studentArr, setStudentArr] = useState(
    state.students?.length
    ? state.students
    : [ generateBlankStudentData() ]
  );

  const addStudent = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    // Grab latest values from form before adding new one
    const formData = getValues();
    const [, studentsFromForm] = splitStudentsFromFormData(formData);

    const newStudentArr = [...studentsFromForm, generateBlankStudentData()];
    setStudentArr(newStudentArr);
  };

  const unregisterStudentFields = (studentIndex: number) => {
    Object.keys(generateBlankStudentData()).forEach((fieldName) => {
      unregister(`student_${studentIndex}_${fieldName}`);
    });
  };

  const removeStudent = (studentIndex: number) => {
    const formData = getValues();
    const [, studentsFromForm] = splitStudentsFromFormData(formData);

    if (studentsFromForm.length > 1) {
      const newStudentArr = [...studentsFromForm];
      newStudentArr.splice(studentIndex, 1);

      // Unregister the fields so the values don't get stuck
      // I think it would be the index and everything afterwards, since it gets shifted back up
      for (let i = studentIndex; i < studentsFromForm.length; i++) {
        unregisterStudentFields(i);
      }

      setStudentArr(newStudentArr);
    } else {
      console.log('You can not remove the only student');
    }
  };

  // Remove all but first student when they click the "individual" radio button
  const changeIsGroup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checkedGroup = (e.target.value === 'group' && e.target.checked)
      || (e.target.value === 'individual' && !e.target.checked)

    if (!checkedGroup) {
      // This is based on removeStudent()
      const formData = getValues();
      const [, studentsFromForm] = splitStudentsFromFormData(formData);

      if (studentsFromForm.length > 1) {
        const newStudentArr = [studentsFromForm[0]];

        // Unregister the fields so the values don't get stuck
        for (let i = 1; i < studentsFromForm.length; i++) {
          unregisterStudentFields(i);
        }

        setStudentArr(newStudentArr);
      }
    }

    applyForm();
  };

  // TODO: you're not supposed to use getValues in a render method...
  const isGroup = getValues().applyingAs
    ? getValues().applyingAs === 'group'
    : state.formData.applyingAs === 'group';

  const studentInfoArr = studentArr.map((studentData: StudentData, index: number) => {
    return <StudentInfo
      errors={errors}
      register={register}
      formData={state.formData}
      getValues={getValues}
      setValue={setValue}
      control={control}
      reviewMode={props.reviewMode}
      returnToStep={props.returnToStep}
      // ...the values don't change when adding/removing if the key doesn't change...
      // ohh, it  re-renders this on error because the key changes. well then.
      // key={Math.random()}
      key={studentData.uuid}
      studentIndex={index}
      studentData={studentData}
      isGroup={isGroup}
      removeStudent={removeStudent}
    />;
  });

  const groupLimitElements = [];
  // If it's a group and not in review mode, show this stuff
  if (isGroup && !props.reviewMode) {
    // If limit not reached, show button
    if (studentInfoArr.length < MAX_GROUP_MEMBERS) {
      groupLimitElements.push(
        <button className="btn AddGroupMemberButton" onClick={addStudent}>
          Add another group member
        </button>
      );
    }
    // Show limit message
    groupLimitElements.push(
      <p className={`AddGroupMemberLimit ${studentInfoArr.length >= MAX_GROUP_MEMBERS && 'AddGroupMemberLimit--reached'}`}>
        { studentInfoArr.length >= MAX_GROUP_MEMBERS
          ? "Member limit reached"
          : `Limit of ${MAX_GROUP_MEMBERS} members (including leader)`
        }
      </p>
    )
  }

  const content = (
    <>
      <h3 className="Form__heading">About you:</h3>

      {/* TODO: or do I need to do some fancy conditional validation with the fields that are/n't hidden? */}
      <fieldset className="Fieldset Fieldset--two-col">
        <legend>I'm applying as:</legend>
        <RadioButton
          name="applyingAs"
          label="A student individual"
          value="individual"
          defaultSelected={true}
          formData={state.formData}
          onChange={changeIsGroup}
          showEditBtn={props.reviewMode ? true : false}
          editable={props.reviewMode ? false : true}
          register={register}
          errors={errors}
          returnToStep={props.returnToStep}
        />

        <RadioButton
          name="applyingAs"
          label="A student group"
          value="group"
          defaultSelected={false}
          formData={state.formData}
          onChange={changeIsGroup}
          showEditBtn={props.reviewMode ? true : false}
          editable={props.reviewMode ? false : true}
          register={register}
          errors={errors}
          returnToStep={props.returnToStep}
        />

        { generateErrorMarkup(errors, 'applyingAs') }
      </fieldset>

      {
        isGroup && !props.reviewMode
        ? <p>Please nominate a group leader and have them fill out the information below:</p>
        : null
      }

      {studentInfoArr}

      {groupLimitElements}

      <TextArea
        name="howDidYouHearAboutThisGrant"
        label="How did you hear about this grant?"
        formData={state.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={register}
        validationObj={{
          required: 'This field is required',
          validate: {
            maxLength: generateMaxLengthValidateFn(1000),
          }
        }}
        errors={errors}
        returnToStep={props.returnToStep}
      />

      <TextInput
        // Fake honey pot field (this isn't used anywhere)
        name="honey"
        label="honey"
        defaultValue=""
        honey={true}
        formData={state.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={register}
        errors={errors}
        returnToStep={props.returnToStep}
      />

      { props.reviewMode ? undefined :
      <div className="bottom-buttons">
        <input type='submit' value="Next" />
        {/* <button onClick={onBack}>Back</button> */}
      </div>
      }
    </>
  );

  console.debug({errors});

  // Wrap in form if not in review mode
  return (
    props.reviewMode
    ? content
    : <FormStepWrapper onSubmit={handleSubmit(onSubmit)} content={content} />
  );
};

export default Step1;
